import PropTypes from 'prop-types';
import { Stack, IconButton, Tooltip } from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  AttachMoney as PaidIcon,
  Undo as RestoreIcon,
  PlayArrow as PlayIcon,
  Person as ImpersonateIcon
} from '@mui/icons-material';

/**
 * Reusable Table Actions Component
 *
 * Provides consistent action buttons for table rows across the application
 *
 * @example
 * <TableActions
 *   onView={() => navigate(`/items/${id}`)}
 *   onEdit={() => navigate(`/items/${id}/edit`)}
 *   onDelete={() => handleDelete(id)}
 * />
 */
const TableActions = ({
  onView,
  onEdit,
  onDelete,
  onSend,
  onApprove,
  onReject,
  onDownload,
  onRefresh,
  onPay,
  onRestore,
  onPlay,
  onImpersonate,
  viewTooltip = 'View Details',
  editTooltip = 'Edit',
  deleteTooltip = 'Delete',
  sendTooltip = 'Send',
  approveTooltip = 'Approve',
  rejectTooltip = 'Reject',
  downloadTooltip = 'Download',
  refreshTooltip = 'Refresh',
  payTooltip = 'Mark as Paid',
  restoreTooltip = 'Restore',
  playTooltip = 'Start',
  impersonateTooltip = 'Login As',
  spacing = 1
}) => {
  return (
    <Stack
      direction="row"
      spacing={spacing}
      sx={{
        justifyContent: 'center'
      }}
    >
      {onView && (
        <Tooltip title={viewTooltip}>
          <IconButton size="small" onClick={onView}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onEdit && (
        <Tooltip title={editTooltip}>
          <IconButton size="small" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onImpersonate && (
        <Tooltip title={impersonateTooltip}>
          <IconButton size="small" color="warning" onClick={onImpersonate}>
            <ImpersonateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onSend && (
        <Tooltip title={sendTooltip}>
          <IconButton size="small" color="primary" onClick={onSend}>
            <SendIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onApprove && (
        <Tooltip title={approveTooltip}>
          <IconButton size="small" color="success" onClick={onApprove}>
            <ApproveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onReject && (
        <Tooltip title={rejectTooltip}>
          <IconButton size="small" color="error" onClick={onReject}>
            <RejectIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onDownload && (
        <Tooltip title={downloadTooltip}>
          <IconButton size="small" color="primary" onClick={onDownload}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onRefresh && (
        <Tooltip title={refreshTooltip}>
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onDelete && (
        <Tooltip title={deleteTooltip}>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onPay && (
        <Tooltip title={payTooltip}>
          <IconButton size="small" color="success" onClick={onPay}>
            <PaidIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onRestore && (
        <Tooltip title={restoreTooltip}>
          <IconButton size="small" color="primary" onClick={onRestore}>
            <RestoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onPlay && (
        <Tooltip title={playTooltip}>
          <IconButton size="small" color="primary" onClick={onPlay}>
            <PlayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

TableActions.propTypes = {
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSend: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onDownload: PropTypes.func,
  onRefresh: PropTypes.func,
  onPay: PropTypes.func,
  onRestore: PropTypes.func,
  onPlay: PropTypes.func,
  onImpersonate: PropTypes.func,
  viewTooltip: PropTypes.string,
  editTooltip: PropTypes.string,
  deleteTooltip: PropTypes.string,
  sendTooltip: PropTypes.string,
  approveTooltip: PropTypes.string,
  rejectTooltip: PropTypes.string,
  downloadTooltip: PropTypes.string,
  refreshTooltip: PropTypes.string,
  payTooltip: PropTypes.string,
  restoreTooltip: PropTypes.string,
  playTooltip: PropTypes.string,
  impersonateTooltip: PropTypes.string,
  spacing: PropTypes.number
};

export default TableActions;
